import getPieveAuthToken from '@/lib/auth/getPieveAuthToken.js';
import hydrateThreads from '@/redux/actions/pieveActions/hydrateThreads.js';
import upsertThreadOptimistic from '@/redux/actions/pieveActions/upsertThreadOptimistic.js';
import { normalizeThreadSandboxMode } from '@/lib/pieveComposerSettings.js';
import normalizeProjectId from '@/lib/normalizeProjectId.js';
import normalizeThreadDebugMode from '@/lib/normalizeThreadDebugMode.js';
import normalizeCreatedThreadRecord from '@/lib/threads/normalizeCreatedThreadRecord.js';
import normalizeThreadComposerSettings from '@/lib/threads/normalizeThreadComposerSettings.js';
import normalizeThreadType from '@/lib/threads/normalizeThreadType.js';
import { createThread as createThreadRequest } from '@/redux/requests/threadsRequests.js';

/** Creates a thread on the server and files it in the store; optionally reloads the thread list after. */
const createThread = ({ title, projectId, sandboxMode, debugMode, composerModel, composerReasoningEffort, threadType, pinned, sidebarPlacement = null, hydrateAfterCreate = true, waitForHydrate = true } = {}) => {
  return async (dispatch) => {
    const shouldPersistComposerSettings = composerModel !== undefined || composerReasoningEffort !== undefined;
    const normalizedComposerSettings = shouldPersistComposerSettings
      ? normalizeThreadComposerSettings({
          model: composerModel,
          reasoning: composerReasoningEffort
        })
      : null;

    const normalizedThreadType = normalizeThreadType(threadType, '') || null;
    const authToken = await getPieveAuthToken();
    const normalizedProjectId = normalizeProjectId(projectId);
    const normalizedSandboxMode = normalizeThreadSandboxMode(sandboxMode, '') || null;
    const normalizedDebugMode = normalizeThreadDebugMode(debugMode, '') || null;
    const createPayload = {
      title: title || 'New task',
      authToken,
      ...(normalizedProjectId ? { projectId: normalizedProjectId } : {}),
      ...(normalizedSandboxMode ? { sandboxMode: normalizedSandboxMode } : {}),
      ...(normalizedDebugMode ? { debugMode: normalizedDebugMode } : {}),
      ...(normalizedThreadType ? { threadType: normalizedThreadType } : {}),
      ...(typeof pinned === 'boolean' ? { pinned } : {}),
      ...(sidebarPlacement ? { sidebarPlacement } : {}),
      ...(normalizedComposerSettings
        ? {
            composerModel: normalizedComposerSettings.modelId,
            composerReasoningEffort: normalizedComposerSettings.reasoningEffort
          }
        : {})
    };
    const createRes = await createThreadRequest(createPayload);

    if (!createRes?.success) {
      return {
        error: {
          message: createRes?.message || 'Failed to create task',
          detail: createRes?.detail || null,
          status: createRes?.status || null
        }
      };
    }

    const thread = normalizeCreatedThreadRecord({
      value: createRes?.data,
      projectId: normalizedProjectId,
      sandboxMode: normalizedSandboxMode,
      debugMode: normalizedDebugMode,
      threadType: normalizedThreadType,
      composerModel: normalizedComposerSettings?.modelId || null,
      composerReasoningEffort: normalizedComposerSettings?.reasoningEffort || null
    });

    if (!thread) {
      return {
        error: {
          message: 'Task create response did not include a task ID'
        }
      };
    }

    dispatch(
      upsertThreadOptimistic({
        thread,
        sidebarOrganizationCommit: createRes?.meta?.sidebarOrganizationCommit || null,
        sidebarOrganizationCommittedAt: Date.now()
      })
    );

    if (hydrateAfterCreate) {
      const hydratePromise = dispatch(
        hydrateThreads({
          threadIdsForEvents: [thread.id]
        })
      );

      if (waitForHydrate) {
        try {
          await hydratePromise;
        } catch {
          // Do not block navigation when create succeeded but hydration fails.
        }
      } else {
        Promise.resolve(hydratePromise).catch(() => {});
      }
    }

    return {
      ...thread,
      didCreateThread: createRes?.data?.created !== false,
      hasEvents: createRes?.data?.hasEvents !== false
    };
  };
};

export default createThread;
